/**
 * GuidedCompletionFlow — Phase 2 (2026-09).
 *
 * Single, mobile-first guided post-event completion experience that replaces
 * the fragmented multi-tab flow AFTER a shift has ended. Walks the talent
 * sequentially through their event requirements with one clear progress
 * indicator ("X of N requirements completed"), a "Continue to next incomplete"
 * jump, and a sticky bottom bar that shows "Complete Event Requirements" until
 * everything required is done — then unlocks "Finish Event".
 *
 * Reuses the existing, working sub-systems (JobPhotoUpload with auto-retry,
 * PostEventRecapModal / RatingFlow, ShiftMetricsWizard, TalentExpensesPanel)
 * inside the new step cards — no working logic was rebuilt.
 *
 * INVARIANTS (per owner):
 *  • Attendance, Payment and Documentation statuses are shown together.
 *  • Payment is placed ON HOLD while any event requirement is outstanding and
 *    is released only once everything is completed and verified.
 *  • A photo only counts once the SERVER confirms the upload.
 *  • Event Recap is REQUIRED — Finish Event stays locked until the backend
 *    confirms every required item is complete.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CheckCircle2, Circle, ArrowRight, ArrowDown, Loader2, Image as ImageIcon,
  ClipboardCheck, BarChart3, Receipt, ListChecks, PartyPopper, ShieldCheck,
} from 'lucide-react';
import api from '../../utils/api';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import JobPhotoUpload from '../JobPhotoUpload';
import TalentExpensesPanel from '../TalentExpensesPanel';
import ShiftMetricsWizard from '../ShiftMetricsWizard';
import { PostEventRecapModal } from '../PostEventRecapModal';

const chip = (label, cls, testid) => (
  <span
    data-testid={testid}
    className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold border ${cls}`}
  >
    {label}
  </span>
);

const attendanceChip = (s) => {
  switch (s) {
    case 'verified': return chip('Worked & Verified', 'bg-emerald-100 text-emerald-800 border-emerald-300', 'attendance-status-chip');
    case 'worked': return chip('Worked', 'bg-emerald-50 text-emerald-700 border-emerald-200', 'attendance-status-chip');
    case 'needs_verification': return chip('Attendance Pending', 'bg-amber-50 text-amber-700 border-amber-200', 'attendance-status-chip');
    case 'no_show': return chip('No Show', 'bg-rose-50 text-rose-700 border-rose-200', 'attendance-status-chip');
    default: return chip('Scheduled', 'bg-slate-100 text-slate-700 border-slate-200', 'attendance-status-chip');
  }
};

const paymentChip = (s) => {
  switch (s) {
    case 'paid': return chip('Paid', 'bg-emerald-100 text-emerald-800 border-emerald-300', 'payment-status-chip');
    case 'processing': return chip('Payment Processing', 'bg-cyan-50 text-cyan-700 border-cyan-200', 'payment-status-chip');
    case 'on_hold': return chip('On Hold: Incomplete Event Requirements', 'bg-rose-50 text-rose-700 border-rose-300', 'payment-status-chip');
    default: return chip('Payment Pending', 'bg-slate-100 text-slate-700 border-slate-200', 'payment-status-chip');
  }
};

const docChip = (s) => (s === 'complete'
  ? chip('Docs Complete', 'bg-emerald-50 text-emerald-700 border-emerald-200', 'documentation-status-chip')
  : chip('Docs Incomplete', 'bg-amber-50 text-amber-800 border-amber-300', 'documentation-status-chip'));

const StatusBadge = ({ status }) => {
  const map = {
    complete: { label: 'Complete', cls: 'bg-emerald-100 text-emerald-800 border-emerald-300', Icon: CheckCircle2 },
    incomplete: { label: 'Incomplete', cls: 'bg-amber-50 text-amber-800 border-amber-300', Icon: Circle },
    optional: { label: 'Optional', cls: 'bg-slate-100 text-slate-600 border-slate-200', Icon: Circle },
    not_required: { label: 'Not Required', cls: 'bg-slate-100 text-slate-500 border-slate-200', Icon: Circle },
  };
  const cfg = map[status] || map.incomplete;
  const { Icon } = cfg;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border ${cfg.cls}`}>
      <Icon className="w-3 h-3" /> {cfg.label}
    </span>
  );
};

const StepCard = ({ stepKey, icon: Icon, title, description, status, children, testid }) => (
  <div
    id={`step-${stepKey}`}
    data-testid={testid}
    data-step-status={status}
    className={`bg-white border rounded-xl p-4 shadow-sm transition-all scroll-mt-24 ${
      status === 'incomplete' ? 'border-amber-300 ring-1 ring-amber-100' : 'border-slate-200'
    }`}
  >
    <div className="flex items-start justify-between gap-3 mb-2">
      <div className="flex items-start gap-2.5 min-w-0">
        <span className="w-9 h-9 rounded-lg bg-cyan-50 text-cyan-700 grid place-items-center shrink-0">
          <Icon className="w-4.5 h-4.5" />
        </span>
        <div className="min-w-0">
          <div className="text-base font-semibold text-slate-900 leading-tight">{title}</div>
          <p className="text-xs text-slate-500 mt-0.5">{description}</p>
        </div>
      </div>
      <StatusBadge status={status} />
    </div>
    <div className="pl-11.5">{children}</div>
  </div>
);

export default function GuidedCompletionFlow({ booking, onViewDetails }) {
  const navigate = useNavigate();
  const bookingId = booking.id || booking.booking_id;
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [finishing, setFinishing] = useState(false);
  const [recapOpen, setRecapOpen] = useState(false);
  const [metricsOpen, setMetricsOpen] = useState(false);

  const [tasksAck, setTasksAck] = useState(() => {
    try { return localStorage.getItem(`ievntur:tasks-ack:${bookingId}`) === '1'; } catch { return false; }
  });

  const fetchState = useCallback(async () => {
    try {
      const res = await api.get(`/payment-readiness/booking/${bookingId}/state`);
      setState(res.data);
    } catch (e) {
      console.error('Failed to load completion state', e);
    } finally {
      setLoading(false);
    }
  }, [bookingId]);

  useEffect(() => { fetchState(); }, [fetchState]);

  const ackTasks = () => {
    try { localStorage.setItem(`ievntur:tasks-ack:${bookingId}`, '1'); } catch { /* ignore */ }
    setTasksAck(true);
  };

  const steps = useMemo(() => {
    if (!state) return [];
    const photosReq = !!state.photos_required;
    const metricsReq = !!state.recap_metrics_required;
    return [
      {
        key: 'tasks', required: false,
        status: tasksAck ? 'complete' : 'optional',
      },
      {
        key: 'photos', required: photosReq,
        status: !photosReq ? 'not_required'
          : (state.photos_submitted_count || 0) > 0 ? 'complete' : 'incomplete',
      },
      {
        key: 'metrics', required: metricsReq,
        status: !metricsReq ? 'not_required'
          : state.recap_metrics_submitted ? 'complete' : 'incomplete',
      },
      {
        key: 'recap', required: true,
        status: state.rating_submitted ? 'complete' : 'incomplete',
      },
      {
        key: 'reimbursements', required: false,
        status: 'optional',
      },
    ];
  }, [state, tasksAck]);

  const requiredSteps = steps.filter((s) => s.required);
  const completedRequired = requiredSteps.filter((s) => s.status === 'complete').length;
  const totalRequired = requiredSteps.length;
  const allRequiredDone = totalRequired > 0 && completedRequired === totalRequired;
  const pct = totalRequired ? Math.round((completedRequired / totalRequired) * 100) : 0;
  const finished = !!state?.requirements_finished;

  const firstIncompleteKey = (steps.find((s) => s.required && s.status === 'incomplete') || {}).key;

  const scrollToNext = () => {
    if (!firstIncompleteKey) return;
    const el = document.getElementById(`step-${firstIncompleteKey}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const finishEvent = async () => {
    setFinishing(true);
    try {
      const res = await api.post(`/payment-readiness/booking/${bookingId}/finish-requirements`);
      toast.success(res.data?.already_finished ? 'Event already completed' : 'Event completed — nice work!');
      await fetchState();
      setTimeout(() => navigate('/talent/bookings-v2'), 900);
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = (detail && typeof detail === 'object') ? detail.message : (detail || 'Could not finish — please complete every required item first.');
      toast.error(msg);
      await fetchState();
    } finally {
      setFinishing(false);
    }
  };

  if (loading) {
    return <div className="min-h-[40vh] grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-cyan-600" /></div>;
  }
  if (!state) {
    return (
      <div className="p-6 text-center text-sm text-slate-600" data-testid="guided-flow-error">
        Could not load your event requirements. Please try again.
      </div>
    );
  }

  return (
    <div className="pb-28" data-testid="post-event-completion-flow">
      {/* Status chips */}
      <div className="flex flex-wrap gap-2 mb-3" data-testid="completion-status-chips">
        {attendanceChip(state.attendance_status)}
        {paymentChip(state.payment_state)}
        {docChip(state.documentation_status)}
      </div>

      {/* Progress summary */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm mb-3">
        {finished ? (
          <div className="flex items-center gap-3" data-testid="completion-finished-banner">
            <span className="w-10 h-10 rounded-full bg-emerald-100 text-emerald-700 grid place-items-center">
              <PartyPopper className="w-5 h-5" />
            </span>
            <div>
              <div className="text-base font-bold text-slate-900">Event requirements completed</div>
              <p className="text-xs text-slate-500">
                Submitted {state.requirements_finished_at ? new Date(state.requirements_finished_at).toLocaleString() : ''}. Any payment hold for event requirements has been lifted — your payment will proceed once your hours are verified.
              </p>
            </div>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between gap-2 mb-2">
              <div className="text-sm font-bold text-slate-900" data-testid="completion-progress-summary">
                {completedRequired} of {totalRequired} requirement{totalRequired === 1 ? '' : 's'} completed
              </div>
              <div className="text-xs font-semibold text-cyan-700">{pct}%</div>
            </div>
            <div className="w-full h-2.5 bg-slate-100 rounded-full overflow-hidden border border-slate-200" data-testid="completion-progress-bar">
              <div className="h-full bg-cyan-600 transition-all" style={{ width: `${pct}%` }} />
            </div>
            {firstIncompleteKey && (
              <button
                type="button"
                onClick={scrollToNext}
                className="mt-3 inline-flex items-center gap-1.5 text-xs font-bold text-cyan-700 hover:text-cyan-900"
                data-testid="continue-to-next-incomplete-btn"
              >
                <ArrowDown className="w-3.5 h-3.5" /> Continue to next item
              </button>
            )}
          </>
        )}
        {onViewDetails && (
          <button
            type="button"
            onClick={onViewDetails}
            className="mt-3 ml-4 inline-flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-slate-800"
            data-testid="view-event-details-btn"
          >
            View full event details
          </button>
        )}
      </div>

      <div className="space-y-3">
        {/* 1 · Tasks */}
        <StepCard
          stepKey="tasks" testid="step-card-tasks" icon={ListChecks}
          title="Tasks & Briefing"
          description="Confirm you completed your event tasks, dress code and on-site briefing."
          status={tasksAck ? 'complete' : 'optional'}
        >
          {!tasksAck ? (
            <Button size="sm" variant="outline" onClick={ackTasks} data-testid="ack-tasks-btn">
              <CheckCircle2 className="w-4 h-4 mr-1.5" /> Mark tasks reviewed
            </Button>
          ) : (
            <p className="text-xs text-emerald-700 flex items-center gap-1.5">
              <CheckCircle2 className="w-3.5 h-3.5" /> Tasks reviewed
            </p>
          )}
        </StepCard>

        {/* 2 · Photos */}
        <StepCard
          stepKey="photos" testid="step-card-photos" icon={ImageIcon}
          title="Event Photos"
          description={state.photos_required
            ? 'Upload the setup, crowd and display photos the client requires. Each photo counts only once the server confirms it.'
            : 'No photos are required for this role.'}
          status={!state.photos_required ? 'not_required'
            : (state.photos_submitted_count || 0) > 0 ? 'complete' : 'incomplete'}
        >
          {state.photos_required && (
            <>
              {(state.photos_required_min > 0 || (state.photos_required_shots || []).length > 0) && (
                <div className="text-[11px] text-slate-600 mb-2 rounded-md bg-slate-50 border border-slate-200 p-2" data-testid="photo-requirements-summary">
                  {state.photos_required_min > 0 && (
                    <div className="flex items-center gap-1.5">
                      <span className={((state.photos_submitted_count || 0) >= state.photos_required_min) ? 'text-emerald-700 font-semibold' : 'text-slate-600'}>
                        {(state.photos_submitted_count || 0) >= state.photos_required_min ? '✓' : '•'} At least {state.photos_required_min} photo{state.photos_required_min === 1 ? '' : 's'} ({state.photos_submitted_count || 0} so far)
                      </span>
                    </div>
                  )}
                  {(state.photos_required_shots || []).length > 0 && (
                    <div className="mt-1">
                      <span className="text-slate-500">Required shots: </span>
                      {state.photos_required_shots.map((s) => {
                        const done = (state.photos_shots_covered || []).includes(s);
                        return (
                          <span key={s} className={`inline-block mr-1.5 px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${done ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`} data-testid={`shot-status-${s}`}>
                            {done ? '✓ ' : ''}{s}
                          </span>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
              {(state.photos_submitted_count || 0) > 0 && (
                <p className="text-xs text-emerald-700 mb-2 flex items-center gap-1.5">
                  <ShieldCheck className="w-3.5 h-3.5" /> {state.photos_submitted_count} photo{state.photos_submitted_count === 1 ? '' : 's'} confirmed by server
                </p>
              )}
              <JobPhotoUpload bookingId={bookingId} shiftId={state.shift_id || undefined} requiredShots={state.photos_required_shots || []} onUploaded={fetchState} />
            </>
          )}
        </StepCard>

        {/* 3 · Metrics */}
        <StepCard
          stepKey="metrics" testid="step-card-metrics" icon={BarChart3}
          title="Event Metrics"
          description={state.recap_metrics_required
            ? 'Report sales, sample counts, attendee interactions and foot traffic.'
            : 'No metrics are required for this role.'}
          status={!state.recap_metrics_required ? 'not_required'
            : state.recap_metrics_submitted ? 'complete' : 'incomplete'}
        >
          {state.recap_metrics_required && (
            state.recap_metrics_submitted ? (
              <Button size="sm" variant="outline" onClick={() => setMetricsOpen(true)} data-testid="open-metrics-btn">
                View / Update Metrics
              </Button>
            ) : (
              <Button size="sm" className="bg-cyan-600 hover:bg-cyan-700" onClick={() => setMetricsOpen(true)} data-testid="open-metrics-btn">
                <BarChart3 className="w-4 h-4 mr-1.5" /> Log Metrics
              </Button>
            )
          )}
        </StepCard>

        {/* 4 · Recap (always required) */}
        <StepCard
          stepKey="recap" testid="step-card-recap" icon={ClipboardCheck}
          title="Post-Event Recap & Rating"
          description="Required — the client needs your recap to know how the event went."
          status={state.rating_submitted ? 'complete' : 'incomplete'}
        >
          {state.rating_submitted ? (
            <p className="text-xs text-emerald-700 flex items-center gap-1.5">
              <CheckCircle2 className="w-3.5 h-3.5" /> Recap submitted
            </p>
          ) : (
            <Button size="sm" className="bg-cyan-600 hover:bg-cyan-700" onClick={() => setRecapOpen(true)} data-testid="submit-recap-btn">
              <ClipboardCheck className="w-4 h-4 mr-1.5" /> Start Recap
            </Button>
          )}
        </StepCard>

        {/* 5 · Reimbursements (optional) */}
        <StepCard
          stepKey="reimbursements" testid="step-card-reimbursements" icon={Receipt}
          title="Reimbursements"
          description="Optional — upload receipts for approved parking, mileage or supplies."
          status="optional"
        >
          <TalentExpensesPanel bookingId={bookingId} bookingStatus={booking.status} />
        </StepCard>
      </div>

      {/* Sticky bottom action bar */}
      <div className="fixed bottom-0 inset-x-0 bg-white/95 backdrop-blur-md border-t border-slate-200 p-3 sm:px-6 z-40 shadow-lg">
        {finished ? (
          <Button
            className="w-full min-h-[48px] bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-xl"
            onClick={() => navigate('/talent/bookings-v2')}
            data-testid="finished-back-btn"
          >
            <CheckCircle2 className="w-4 h-4 mr-1.5" /> Event Completed — Back to Bookings
          </Button>
        ) : allRequiredDone ? (
          <Button
            className="w-full min-h-[48px] bg-cyan-600 hover:bg-cyan-700 text-white font-bold rounded-xl active:scale-[0.98] transition-all"
            onClick={finishEvent}
            disabled={finishing}
            data-testid="finish-event-bottom-btn"
          >
            {finishing ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <PartyPopper className="w-4 h-4 mr-1.5" />}
            Finish Event
          </Button>
        ) : (
          <Button
            className="w-full min-h-[48px] bg-cyan-600 hover:bg-cyan-700 text-white font-bold rounded-xl active:scale-[0.98] transition-all"
            onClick={scrollToNext}
            data-testid="complete-event-requirements-btn"
          >
            Complete Event Requirements ({completedRequired}/{totalRequired})
            <ArrowRight className="w-4 h-4 ml-1.5" />
          </Button>
        )}
      </div>

      {/* Reused sub-flows */}
      <PostEventRecapModal
        isOpen={recapOpen}
        onClose={() => { setRecapOpen(false); fetchState(); }}
        booking={booking}
        onRecapSubmitted={() => { setRecapOpen(false); fetchState(); }}
      />
      {metricsOpen && (
        <div
          className="fixed inset-0 z-[60] bg-black/40 flex items-start justify-center p-3 overflow-y-auto"
          data-testid="metrics-wizard-overlay"
          onClick={(e) => { if (e.target === e.currentTarget) setMetricsOpen(false); }}
        >
          <div className="bg-white rounded-xl p-4 w-full max-w-2xl my-6 max-h-[95vh] overflow-y-auto">
            <ShiftMetricsWizard
              bookingId={bookingId}
              onClose={() => setMetricsOpen(false)}
              onSubmitted={() => { setMetricsOpen(false); fetchState(); }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
